import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { AgeingCountsDetails } from "../../../../../api/services/program/models";
import { useGetMyCaseLoadsParticipantsDetails } from "../../../../../api/services/program/programApis";
import { ReactComponent as UserIcon } from "../../../../../assets/images/user-circle.svg";
import {
  IconText,
  ITabs,
  MuiBox,
  MuiTabPanel,
  MuiTabs,
  resolveContent,
} from "../../../../../components";
import { PAGE_IDS } from "../../../../../helpers/constants";
import {
  getValueOrBlank,
  getValueOrBlankArray,
  isEmpty,
} from "../../../../../helpers/utils";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../redux/slices/appState/appStateSlice";
import {
  AGEING_FILTER_KEYS,
  MY_CASELOADS_TABS_KEYS,
} from "../../../modelsAndSchemas/Models";
import { myCaseloadsSiteCoreKeys } from "../../../sitecore/SiteCoreKeys";
import { MyCaseloadsWrapper } from "./MyCaseLoads.style";
import { CPParticipantsResults } from "./components/cpParticipantsResults/CPParticipantsResults";

const pageId = PAGE_IDS.COACH_PORTAL_DASHBOARD_MY_CASELOADS;

interface CPMyCaseloadsProps {
  ageingCountsDetails?: AgeingCountsDetails;
}

export const MyCaseloads: FC<CPMyCaseloadsProps> = ({
  ageingCountsDetails,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { useFetchLabel, fetchComponentTitle, commonLabels } = useSiteCore();
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const {
    myCaseloadsTitle,
    allTab,
    newJoinersTab,
    nearingClosureTab,
    noNearingClosureAvailable,
    noNewJoinersAvailable,
    noCaseloadsAvailable,
  } = myCaseloadsSiteCoreKeys;
  const getLabel = useFetchLabel(myCaseloadsTitle);

  const {
    data: newJoinersData,
    isPending: isNewJoinersPending,
    isFetching: isNewJoinersFetching,
    isSuccess: isNewJoinersSuccess,
    isError: isNewJoinersError,
    refetchWithParams: refetchNewJoinersData,
  } = useGetMyCaseLoadsParticipantsDetails();

  const {
    data: nearingClosureData,
    isPending: isNearingClosurePending,
    isFetching: isNearingClosureFetching,
    isSuccess: isNearingClosureSuccess,
    isError: isNearingClosureError,
    refetchWithParams: refetchNearingClosureData,
  } = useGetMyCaseLoadsParticipantsDetails();

  const {
    data: allData,
    isPending: isAllPending,
    isFetching: isAllFetching,
    isSuccess: isAllSuccess,
    isError: isAllError,
    refetchWithParams: refetchAllData,
  } = useGetMyCaseLoadsParticipantsDetails();

  const isAnyQueryFetching =
    isNewJoinersFetching || isNearingClosureFetching || isAllFetching;

  useEffect(() => {
    return () => {
      dispatch(
        toggleLoader({
          isMyCaseLoadsPending: {
            isVisible: false,
          },
        }),
      );
    };
  }, []);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isMyCaseLoadsPending: {
          isVisible: isAnyQueryFetching,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
  }, [isAnyQueryFetching]);

  useEffect(() => {
    return () => {
      dispatch(
        toggleLoader({
          isMyCaseLoadsPending: {
            isVisible: false,
          },
        }),
      );
    };
  }, []);

  useEffect(() => {
    if (isNewJoinersError || isNearingClosureError || isAllError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
    }
  }, [isNewJoinersError, isNearingClosureError, isAllError]);

  const renderNewJoinersTab = useMemo(
    () => (
      <CPParticipantsResults
        rows={getValueOrBlankArray(newJoinersData)}
        emptyMessageKey={noNewJoinersAvailable}
      />
    ),
    [newJoinersData],
  );
  const renderNearingClosureTab = useMemo(
    () => (
      <CPParticipantsResults
        rows={getValueOrBlankArray(nearingClosureData)}
        emptyMessageKey={noNearingClosureAvailable}
      />
    ),
    [nearingClosureData],
  );
  const renderAllTab = useMemo(
    () => (
      <CPParticipantsResults
        rows={getValueOrBlankArray(allData)}
        emptyMessageKey={noCaseloadsAvailable}
      />
    ),
    [allData],
  );

  const newJoinersCount = Number(ageingCountsDetails?.newJoiners ?? 0);
  const nearingClosureCount = Number(ageingCountsDetails?.nearingClosure ?? 0);
  const allCount = Number(ageingCountsDetails?.all ?? 0);

  const tabIdNewJoiners = `${MY_CASELOADS_TABS_KEYS.NEW_JOINERS}-tab`;
  const tabIdNearingClosure = `${MY_CASELOADS_TABS_KEYS.NEARING_CLOSURE}-tab`;
  const tabIdAll = `${MY_CASELOADS_TABS_KEYS.ALL}-tab`;

  const tabsMap: Record<string, ITabs> = {
    [MY_CASELOADS_TABS_KEYS.NEW_JOINERS]: {
      id: tabIdNewJoiners,
      label: getLabel(newJoinersTab),
      count: newJoinersCount.toString(),
      content: () => renderNewJoinersTab,
    },
    [MY_CASELOADS_TABS_KEYS.NEARING_CLOSURE]: {
      id: tabIdNearingClosure,
      label: getLabel(nearingClosureTab),
      count: nearingClosureCount.toString(),
      content: () => renderNearingClosureTab,
    },
    [MY_CASELOADS_TABS_KEYS.ALL]: {
      id: tabIdAll,
      label: getLabel(allTab),
      count: allCount.toString(),
      content: () => renderAllTab,
    },
  };

  // Predefined orders for each scenario
  const orders: Record<string, (keyof typeof tabsMap)[]> = {
    allNonZero: [
      MY_CASELOADS_TABS_KEYS.NEW_JOINERS,
      MY_CASELOADS_TABS_KEYS.NEARING_CLOSURE,
      MY_CASELOADS_TABS_KEYS.ALL,
    ],
    newJoinersZero: [
      MY_CASELOADS_TABS_KEYS.NEARING_CLOSURE,
      MY_CASELOADS_TABS_KEYS.NEW_JOINERS,
      MY_CASELOADS_TABS_KEYS.ALL,
    ],
    nearingClosureZero: [
      MY_CASELOADS_TABS_KEYS.NEW_JOINERS,
      MY_CASELOADS_TABS_KEYS.NEARING_CLOSURE,
      MY_CASELOADS_TABS_KEYS.ALL,
    ],
    allZero: [
      MY_CASELOADS_TABS_KEYS.ALL,
      MY_CASELOADS_TABS_KEYS.NEW_JOINERS,
      MY_CASELOADS_TABS_KEYS.NEARING_CLOSURE,
    ],
  };

  let selectedOrder: (keyof typeof tabsMap)[];

  if (newJoinersCount === 0 && nearingClosureCount === 0 && allCount === 0) {
    selectedOrder = orders.allZero;
  } else if (newJoinersCount === 0) {
    selectedOrder = orders.newJoinersZero;
  } else if (nearingClosureCount === 0) {
    selectedOrder = orders.nearingClosureZero;
  } else {
    selectedOrder = orders.allNonZero;
  }

  const MY_CASELOADS_TABS = selectedOrder.map((key) => tabsMap[key]);

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTabIndex(value);
  };

  const currentTabContent = resolveContent(
    MY_CASELOADS_TABS[selectedTabIndex]?.content,
  );

  useEffect(() => {
    if (isEmpty(loggedInUser?.id)) return;

    const tabApiConfig = {
      [tabIdNewJoiners]: {
        isSuccess: isNewJoinersSuccess,
        isPending: isNewJoinersPending,
        isError: isNewJoinersError,
        refetch: refetchNewJoinersData,
        key: AGEING_FILTER_KEYS.NEW_JOINERS,
      },
      [tabIdNearingClosure]: {
        isSuccess: isNearingClosureSuccess,
        isPending: isNearingClosurePending,
        isError: isNearingClosureError,
        refetch: refetchNearingClosureData,
        key: AGEING_FILTER_KEYS.NEARING_CLOSURE,
      },
      [tabIdAll]: {
        isSuccess: isAllSuccess,
        isPending: isAllPending,
        isError: isAllError,
        refetch: refetchAllData,
        key: AGEING_FILTER_KEYS.ALL,
      },
    };

    const currentTab = MY_CASELOADS_TABS[selectedTabIndex];
    const config = tabApiConfig[currentTab.id];

    if (config && ((!config.isSuccess && config.isPending) || config.isError)) {
      config.refetch(getValueOrBlank(loggedInUser?.id), config.key);
    }
  }, [selectedTabIndex, loggedInUser?.id, currentTabContent]);

  return (
    <MyCaseloadsWrapper>
      <MuiBox className="my-caseloads-container">
        <IconText
          icon={<UserIcon />}
          iconWidth="37px"
          iconHeight="37px"
          typoVariant="h2SemiBold"
          title={fetchComponentTitle(myCaseloadsTitle)}
        />

        <MuiTabs
          tabs={MY_CASELOADS_TABS}
          value={selectedTabIndex}
          onChange={onTabChange}
          tabsTestIds={MY_CASELOADS_TABS.map(
            (_, index) => `${pageId}-tab${index + 1}`,
          )}
          className="program-participant-tab"
        >
          <MuiTabPanel
            panelId={`${pageId}-tab${selectedTabIndex + 1}`}
            value={selectedTabIndex}
            index={selectedTabIndex}
            isPanelWithMount
            ariaLabelledById={MY_CASELOADS_TABS[selectedTabIndex]?.id}
          >
            {currentTabContent}
          </MuiTabPanel>
        </MuiTabs>
      </MuiBox>
    </MyCaseloadsWrapper>
  );
};
